import React, { useState, useRef, useEffect } from 'react';
import { useUsage } from '../contexts/UsageContext';
import { paidContentRestricted } from '../lib/platform';
import './UsageMeter.css';

// Compact "23k / 50k today" chip with a progress bar. Click pops a
// detail card showing daily + monthly usage for both tokens AND
// images, plus a Subscribe CTA for free users. Refreshes on its own
// via UsageContext — anything that bumps the snapshot (chat reply,
// image generated, manual refresh) flows in automatically.
//
// onSubscribe is wired from the parent (Header / App) so the same
// "force sign-up if anon, otherwise stub-subscribe" flow stays in
// one place. onRequestLogin opens the sign-in modal for the
// anon→free upgrade path (sign-in alone bumps limits 10×).
function UsageMeter({ onSubscribe, onRequestLogin, compact = false }) {
  const { usage } = useUsage();
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!usage || !usage.limits) return null;

  const tDay   = usage.tokensToday   || 0;
  const tMonth = usage.tokensMonth   || 0;
  const iDay   = usage.imagesToday   || 0;
  const iMonth = usage.imagesMonth   || 0;
  const lim    = usage.limits;
  const tDayPct   = pct(tDay,   lim.tokensDay);
  const tMonthPct = pct(tMonth, lim.tokensMonth);
  const iDayPct   = pct(iDay,   lim.imagesDay);
  const iMonthPct = pct(iMonth, lim.imagesMonth);

  // Drive the chip bar from whichever counter is most used — it's
  // the one closest to blocking the user.
  const headlinePct = Math.max(tDayPct, tMonthPct, iDayPct, iMonthPct);
  const tone = headlinePct >= 95 ? 'crit' : headlinePct >= 80 ? 'warn' : 'ok';

  // Label distinguishes the THREE real tiers — anon (browser-only),
  // free (signed in, no sub), pro (paid). Previously anon was
  // mislabelled "Free", so users hitting the 5k anon token cap thought
  // they were on the 50k free tier and the limit was broken.
  const planLabel = usage.plan === 'pro' ? 'Pro'
                  : usage.plan === 'anon' ? 'Guest'
                  : 'Free';

  return (
    <div className="usage-meter-wrap" ref={cardRef}>
      <button
        type="button"
        className={`usage-chip usage-chip-${tone}`}
        onClick={() => setOpen((v) => !v)}
        title={`${fmt(tDay)} / ${fmt(lim.tokensDay)} tokens today`}
      >
        <span className="usage-chip-bar"><span style={{ width: `${headlinePct}%` }} /></span>
        <span className="usage-chip-label">
          {compact ? `${fmt(tDay)}` : `${fmt(tDay)} / ${fmt(lim.tokensDay)}`}
        </span>
        {usage.plan === 'pro' && <span className="usage-chip-badge">PRO</span>}
        {usage.plan === 'anon' && <span className="usage-chip-badge usage-chip-badge-anon">GUEST</span>}
      </button>

      {open && (
        <div className="usage-card">
          <div className="usage-card-head">
            <span className="usage-card-title">Your usage</span>
            <span className={`usage-card-plan usage-card-plan-${usage.plan}`}>
              {planLabel}
            </span>
          </div>

          <UsageRow label="Tokens today"   used={tDay}   max={lim.tokensDay}   />
          <UsageRow label="Tokens month"   used={tMonth} max={lim.tokensMonth} />
          <UsageRow label="Images today"   used={iDay}   max={lim.imagesDay}   imageStyle />
          <UsageRow label="Images month"   used={iMonth} max={lim.imagesMonth} imageStyle />

          <div className="usage-card-reset">
            Daily resets <ResetIn ms={usage.dailyResetMs} /> · Monthly resets <ResetIn ms={usage.monthlyResetMs} />
          </div>

          {/* Anon → sign in (10× bump, free).  Free → upgrade to Pro
              (paid). Pro → no CTA, the user already has full limits. */}
          {usage.plan === 'anon' ? (
            <button
              type="button"
              className="usage-card-cta usage-card-cta-anon"
              onClick={() => { setOpen(false); onRequestLogin?.(); }}
            >
              Sign in for 10× limits
              <span className="usage-card-cta-sub">
                Free account · 50k tokens/day, 20 images/day
              </span>
            </button>
          ) : usage.plan !== 'pro' && !paidContentRestricted() && (
            // iOS hides this CTA — Apple rejects any paid-content
            // reference without a submitted IAP. Web/Android keep it.
            <button
              type="button"
              className="usage-card-cta"
              onClick={() => { setOpen(false); onSubscribe?.(); }}
            >
              Upgrade to Pro · $0.7/day
              <span className="usage-card-cta-sub">
                10× limits · billed monthly
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function UsageRow({ label, used, max, imageStyle }) {
  const p = pct(used, max);
  const tone = p >= 95 ? 'crit' : p >= 80 ? 'warn' : 'ok';
  return (
    <div className="usage-row">
      <div className="usage-row-head">
        <span className="usage-row-label">{label}</span>
        <span className="usage-row-value">
          {imageStyle ? `${used} / ${max}` : `${fmt(used)} / ${fmt(max)}`}
        </span>
      </div>
      <div className={`usage-row-bar usage-row-bar-${tone}`}>
        <div className="usage-row-bar-fill" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function ResetIn({ ms }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, ms - now);
  return <span>{formatDuration(diff)}</span>;
}

function pct(used, max) {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
}
function fmt(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}
function formatDuration(ms) {
  if (ms <= 0) return 'now';
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `in ${d}d ${h}h`;
  if (h > 0) return `in ${h}h ${m}m`;
  return `in ${m}m`;
}

export default UsageMeter;
